/**
 * 程序化海岛生成器 - 创建逼真的3D海岛模型
 */

import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';

export class IslandGenerator {
    constructor() {
        // 多层噪声参数 - 用于创建更自然的不规则形状
        this.primaryNoise = 0.02; // 主要地形噪声
        this.detailNoise = 0.015; // 进一步降低细节噪声，减少碎纹理
        this.coastNoise = 0.02;   // 再降海岸线噪声，边缘更顺滑
        this.heightScale = 10;    // 再降高度，整体更平缓
        this.falloffStrength = 1.1; // 边缘稍柔和

        // 岛屿配置
        this.islandRadius = 17.5; // 缩小一半的基础范围
        this.beachWidth = 2.5;     // 相应缩小海滩宽度
        this.segments = 256; // 平衡分辨率和性能，配合平滑算法

        // 真正不规则形状参数 - 极大增强不规则性
        this.shapeVariation = 1.5;  // 极大增加形状变化，创造极不规则的轮廓
        this.coastComplexity = 20;   // 极大增加海岸线复杂度，创造更多锯齿状边缘
        this.organicFactor = 1.8;  // 大幅增加有机形状因子
        this.fragmentationLevel = 0.6; // 大幅增加破碎化，创造大量海湾和半岛
    }

    /**
     * 创建完整的海岛模型
     */
    createIsland(position = { x: 0, y: 0, z: 0 }) {
        const islandGroup = new THREE.Group();

        // 1. 创建主要地形
        const terrain = this.createTerrain();
        islandGroup.add(terrain);

        // 2. 添加植被（大幅增加密度）
        const vegetation = this.createVegetation();
        islandGroup.add(vegetation);

        // 3. 添加海滩细节
        const beachDetails = this.createBeachDetails();
        islandGroup.add(beachDetails);

        // 4. 添加岩石
        const rocks = this.createRocks();
        islandGroup.add(rocks);

        // 5. 添加微观细节
        const microDetails = this.createMicroDetails();
        islandGroup.add(microDetails);

        // 6. 添加水边植物
        const wetlandPlants = this.createWetlandPlants();
        islandGroup.add(wetlandPlants);

        // 7. 添加木质码头（卡通风格）
        const dock = this.createDock();
        islandGroup.add(dock);

        // 设置位置
        islandGroup.position.set(position.x, position.y, position.z);

        return islandGroup;
    }

    /**
     * Cartoon 压缩高度映射：压低山体，低地更平缓
     */
    cartoonizeHeight(h) {
        const low = this.heightScale * 0.12; // 低地阈值
        const mid = this.heightScale * 0.3;  // 中地阈值
        if (h <= low) {
            return h * 0.75; // 低地更平缓
        } else if (h <= mid) {
            return low + (h - low) * 0.5; // 中地更强压缩
        } else {
            // 山体显著扁平化
            return low + (mid - low) * 0.5 + (h - mid) * 0.2;
        }
    }

    /**
     * 创建木质码头（靠近海岸，向海延伸）
     */
    createDock() {
        const dockGroup = new THREE.Group();

        // 基础参数（卡通比例）
        const length = 10; // 码头长度（向海）
        const width = 2.5; // 码头宽度
        const deckThickness = 0.25;
        const postHeight = 1.6;
        const postRadius = 0.12;

        // 选择一个朝向（面对外海）
        const angle = -Math.PI / 4; // 45° 朝向
        const shoreR = this.islandRadius * 0.85;
        const baseX = Math.cos(angle) * shoreR;
        const baseZ = Math.sin(angle) * shoreR;
        const baseY = this.getHeightAtPosition(baseX, baseZ);

        // 码头中心放在向海半段处
        const centerX = baseX + Math.cos(angle) * (length * 0.5);
        const centerZ = baseZ + Math.sin(angle) * (length * 0.5);
        const centerY = Math.max(baseY + deckThickness * 0.5, 0.35); // 稍高于沙滩/海面

        // 木板平台
        const deckGeo = new THREE.BoxGeometry(length, deckThickness, width);
        const deckMat = new THREE.MeshStandardMaterial({
            color: 0xA16B3B,
            roughness: 0.85,
            metalness: 0.0
        });
        const deck = new THREE.Mesh(deckGeo, deckMat);
        deck.position.set(0, 0, 0);
        deck.castShadow = true;
        deck.receiveShadow = true;
        dockGroup.add(deck);

        // T 型横梁（在最外端）
        const crossGeo = new THREE.BoxGeometry(width * 3.0, deckThickness, width * 1.1);
        const cross = new THREE.Mesh(crossGeo, deckMat);
        cross.position.set(length / 2 - (deckThickness * 0.1), 0, 0);
        cross.castShadow = true;
        cross.receiveShadow = true;
        dockGroup.add(cross);

        // 柱子（四角 + 中间两排）
        const postMat = new THREE.MeshStandardMaterial({
            color: 0x8B5A2B,
            roughness: 0.9,
            metalness: 0.0
        });
        const postGeo = new THREE.CylinderGeometry(postRadius, postRadius, postHeight, 8);

        const postZ = (width / 2) - 0.25;
        const xPositions = [-length / 2 + 0.6, -length / 6, length / 6, length / 2 - 0.6];
        for (const px of xPositions) {
            for (const pz of [-postZ, postZ]) {
                const post = new THREE.Mesh(postGeo, postMat);
                post.position.set(px, -postHeight / 2, pz);
                post.castShadow = true;
                dockGroup.add(post);
            }
        }

        // 扶手（简化）
        const railMat = new THREE.MeshStandardMaterial({ color: 0x8B5A2B, roughness: 0.9, metalness: 0.0 });
        const railGeo = new THREE.CylinderGeometry(0.06, 0.06, length - 1.0, 8);
        const railL = new THREE.Mesh(railGeo, railMat);
        railL.rotation.z = Math.PI / 2;
        railL.position.set(0, 0.35, postZ);
        dockGroup.add(railL);
        const railR = railL.clone();
        railR.position.z = -postZ;
        dockGroup.add(railR);

        // 放置整个码头
        dockGroup.position.set(centerX, centerY, centerZ);
        dockGroup.rotation.y = angle;
        return dockGroup;
    }

    /**
     * 创建地形基础 - 不规则边界，无水域
     */
    createTerrain() {
        // 创建自定义几何体，只包含陆地部分
        return this.createLandOnlyGeometry();
    }

    /**
     * 创建只包含陆地的几何体 - 完全不规则边界版本
     */
    createLandOnlyGeometry() {
        const vertices = [];
        const colors = [];
        const indices = [];
        const normals = [];

        // 动态边界检测 - 不再使用固定网格范围
        const resolution = this.segments;
        const baseRadius = this.islandRadius;
        
        // 动态计算实际边界范围
        const boundarySamples = 200;
        let maxActualRadius = 0;
        
        // 采样检测实际岛屿边界
        for (let i = 0; i < boundarySamples; i++) {
            const angle = (i / boundarySamples) * Math.PI * 2;
            for (let r = baseRadius * 0.5; r < baseRadius * 2.5; r += baseRadius * 0.1) {
                const x = Math.cos(angle) * r;
                const z = Math.sin(angle) * r;
                const height = this.calculateIrregularHeight(x, z);
                
                if (height > 0.05) {
                    maxActualRadius = Math.max(maxActualRadius, r);
                    break;
                }
            }
        }
        
        const actualTerrainSize = maxActualRadius * 2.2; // 基于实际边界
        const step = actualTerrainSize / resolution;

        // 顶点索引映射
        const vertexMap = new Map();
        let vertexIndex = 0;

        // 第一遍：基于实际地形生成顶点，保护山体不被切割
        for (let i = 0; i <= resolution; i++) {
            for (let j = 0; j <= resolution; j++) {
                const x = (i - resolution / 2) * step;
                const z = (j - resolution / 2) * step;
                
                // 计算原始地形高度（不应用边界衰减）
                const rawHeight = this.calculateIrregularHeight(x, z);
                
                // 获取边缘因子但不用于衰减，只用于形状参考
                const edgeFactor = this.calculateEdgeFactor(x, z);
                
                // 保护山体：高地区域不受边界影响
                let finalHeight;
                if (rawHeight > 2.0) {
                    // 高地区域：完全保护，不受边界切割
                    finalHeight = rawHeight;
                } else if (rawHeight > 0.1) {
                    // 中等高度：轻微边界影响
                    finalHeight = rawHeight * Math.max(0.7, edgeFactor);
                } else {
                    // 低地区域：正常边界影响，创造不规则海岸
                    finalHeight = this.calculateBeachHeight(x, z) * edgeFactor;
                }

                // 基于实际地形决定是否生成顶点，而不是固定阈值
                const isLand = rawHeight > 0.05 || finalHeight > 0.2;
                
                if (isLand) { // 基于实际地形存在性
                    vertices.push(x, finalHeight, z);

                    // 使用无水域颜色方案，避免蓝色边界
                    this.addVertexColorNoWater(colors, finalHeight, 0, x, z);

                    // 计算基于实际地形的法线
                    const normal = this.calculateTerrainNormal(x, z);
                    normals.push(normal.x, normal.y, normal.z);

                    // 记录顶点索引
                    vertexMap.set(`${i},${j}`, vertexIndex);
                    vertexIndex++;
                }
            }
        }

        // 第二遍：生成三角形索引，只连接存在的顶点
        for (let i = 0; i < resolution; i++) {
            for (let j = 0; j < resolution; j++) {
                const v1 = vertexMap.get(`${i},${j}`);
                const v2 = vertexMap.get(`${i + 1},${j}`);
                const v3 = vertexMap.get(`${i},${j + 1}`);
                const v4 = vertexMap.get(`${i + 1},${j + 1}`);

                // 智能连接：根据顶点存在情况创建三角形
                if (v1 !== undefined && v2 !== undefined && v3 !== undefined) {
                    indices.push(v1, v2, v3);
                }
                if (v2 !== undefined && v4 !== undefined && v3 !== undefined) {
                    indices.push(v2, v4, v3);
                }
            }
        }

        // 创建几何体
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
        geometry.setIndex(indices);

        // 计算更精确的法线
        geometry.computeVertexNormals();

        // 创建高质量材质
        const material = new THREE.MeshStandardMaterial({
            vertexColors: true,
            side: THREE.FrontSide, // 只渲染正面，避免背面问题
            roughness: 0.7,
            metalness: 0.0,
            flatShading: false,
            transparent: false,
            opacity: 1.0
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.receiveShadow = true;
        mesh.castShadow = true;
        // 为地形网格命名，便于后续识别（GLB导出也会包含此名称）
        mesh.name = 'IslandTerrain';

        return mesh;
    }

    /**
     * 添加顶点颜色 - 基于真实地形特征
     */
    addVertexColor(colors, height, distanceFromCenter, x, z) {
        let r, g, b;

        // 使用地形高度和噪声来决定颜色，而不是距离
        const terrainNoise = this.noise2D(x * 0.05, z * 0.05);
        const coastalFactor = this.getDistanceToCoast(x, z);

        if (height < 0.1) {
            // 水域 - 深蓝色
            r = 0.1; g = 0.3; b = 0.6;
        } else if (coastalFactor < 3 && height < 1) {
            // 海滩 - 明亮沙色，有变化
            const sandVariation = terrainNoise * 0.2;
            r = 0.95 + sandVariation;
            g = 0.85 + sandVariation;
            b = 0.60 + sandVariation * 0.5;
        } else if (height < 1.5) {
            // 低地草原 - 明亮草地绿，有变化
            const grassVariation = terrainNoise * 0.3;
            r = 0.25 + grassVariation;
            g = 0.75 + grassVariation;
            b = 0.25 + grassVariation;
        } else if (height < 5) {
            // 中等高度森林 - 深绿色
            const forestVariation = terrainNoise * 0.2;
            r = 0.15 + forestVariation;
            g = 0.60 + forestVariation;
            b = 0.15 + forestVariation;
        } else if (height < 10) {
            // 高地灌木 - 褐绿色
            const bushVariation = terrainNoise * 0.25;
            r = 0.40 + bushVariation;
            g = 0.50 + bushVariation;
            b = 0.20 + bushVariation;
        } else {
            // 山峰岩石 - 灰褐色
            const rockVariation = terrainNoise * 0.3;
            r = 0.55 + rockVariation;
            g = 0.50 + rockVariation;
            b = 0.40 + rockVariation;
        }

        // 确保颜色值在有效范围内
        r = Math.max(0, Math.min(1, r));
        g = Math.max(0, Math.min(1, g));
        b = Math.max(0, Math.min(1, b));

        colors.push(r, g, b);
    }

    /**
     * 添加顶点颜色 - 无水域版本
     */
    addVertexColorNoWater(colors, height, distanceFromCenter, x, z) {
        let r, g, b;

        // 使用地形高度和噪声来决定颜色，完全去掉水域蓝色
        const terrainNoise = this.noise2D(x * 0.05, z * 0.05);
        const isBeachArea = this.isBeachArea(x, z, height);

        if (isBeachArea) {
            // 海滩 - 明亮沙色，有变化
            const sandVariation = terrainNoise * 0.2;
            r = 0.95 + sandVariation;
            g = 0.85 + sandVariation;
            b = 0.60 + sandVariation * 0.5;
        } else if (height < 1.0) {
            // 低地草原 - 明亮草地绿，有变化
            const grassVariation = terrainNoise * 0.3;
            r = 0.25 + grassVariation;
            g = 0.75 + grassVariation;
            b = 0.25 + grassVariation;
        } else if (height < 3.0) {
            // 中等高度森林 - 深绿色
            const forestVariation = terrainNoise * 0.2;
            r = 0.15 + forestVariation;
            g = 0.60 + forestVariation;
            b = 0.15 + forestVariation;
        } else if (height < 6.0) {
            // 高地灌木 - 褐绿色
            const bushVariation = terrainNoise * 0.25;
            r = 0.40 + bushVariation;
            g = 0.50 + bushVariation;
            b = 0.20 + bushVariation;
        } else {
            // 山峰岩石 - 灰褐色
            const rockVariation = terrainNoise * 0.3;
            r = 0.55 + rockVariation;
            g = 0.50 + rockVariation;
            b = 0.40 + rockVariation;
        }

        // 确保颜色值在有效范围内
        r = Math.max(0, Math.min(1, r));
        g = Math.max(0, Math.min(1, g));
        b = Math.max(0, Math.min(1, b));

        colors.push(r, g, b);
    }

    /**
     * 添加顶点颜色 - 包含沙滩区域
     */
    addVertexColorWithBeach(colors, height, distanceFromCenter, x, z) {
        let r, g, b;

        // 使用地形高度和噪声来决定颜色
        const terrainNoise = this.noise2D(x * 0.05, z * 0.05);

        if (height < 0.5) {
            // 沙滩区域 - 明亮沙色，有自然变化
            const sandVariation = terrainNoise * 0.15;
            r = 0.95 + sandVariation;
            g = 0.87 + sandVariation;
            b = 0.65 + sandVariation * 0.8;
        } else if (height < 1.0) {
            // 沙滩到草地的过渡区域 - 浅绿色
            const transitionFactor = (height - 0.5) / 0.5; // 0到1的过渡
            const sandVariation = terrainNoise * 0.1;

            // 从沙色过渡到草绿色
            r = 0.85 - transitionFactor * 0.5 + sandVariation;
            g = 0.85 - transitionFactor * 0.1 + sandVariation;
            b = 0.60 - transitionFactor * 0.3 + sandVariation;
        } else if (height < 2.0) {
            // 低地草原 - 明亮草地绿
            const grassVariation = terrainNoise * 0.2;
            r = 0.25 + grassVariation;
            g = 0.75 + grassVariation;
            b = 0.25 + grassVariation;
        } else if (height < 4.0) {
            // 中等高度森林 - 深绿色
            const forestVariation = terrainNoise * 0.15;
            r = 0.15 + forestVariation;
            g = 0.60 + forestVariation;
            b = 0.15 + forestVariation;
        } else if (height < 7.0) {
            // 高地灌木 - 褐绿色
            const bushVariation = terrainNoise * 0.2;
            r = 0.40 + bushVariation;
            g = 0.50 + bushVariation;
            b = 0.20 + bushVariation;
        } else {
            // 山峰岩石 - 灰褐色
            const rockVariation = terrainNoise * 0.25;
            r = 0.55 + rockVariation;
            g = 0.50 + rockVariation;
            b = 0.40 + rockVariation;
        }

        // 确保颜色值在有效范围内
        r = Math.max(0, Math.min(1, r));
        g = Math.max(0, Math.min(1, g));
        b = Math.max(0, Math.min(1, b));

        colors.push(r, g, b);
    }

    /**
     * 创建植被
     */
    createVegetation() {
        const vegetationGroup = new THREE.Group();

        // 创建树木
        this.addTrees(vegetationGroup);

        // 创建灌木
        this.addBushes(vegetationGroup);

        // 创建棕榈树（去掉海岸线成圈的棕榈）
        // this.addPalmTrees(vegetationGroup);

        return vegetationGroup;
    }

    /**
     * 添加树木 - 基于地形的有机分布
     */
    addTrees(group) {
        const treeCount = 20; // 再降树木数量，减少细节

        for (let i = 0; i < treeCount; i++) {
            // 使用有机分布而非圆形分布
            const x = (Math.random() - 0.5) * this.islandRadius * 1.8;
            const z = (Math.random() - 0.5) * this.islandRadius * 1.8;
            const y = this.getHeightAtPosition(x, z);

            // 根据地形高度和坡度决定是否放置树木
            if (y > 1.0 && this.isValidTerrainForTrees(x, z, y)) {
                const tree = this.createTree();
                tree.position.set(x, y, z);
                tree.rotation.y = Math.random() * Math.PI * 2;

                // 根据高度和地形复杂度调整树的大小
                const heightFactor = Math.min(1.5, y / 8);
                const terrainFactor = this.getTerrainComplexity(x, z);
                tree.scale.setScalar(0.7 + heightFactor * 0.5 + terrainFactor * 0.3);

                group.add(tree);
            }
        }
    }

    /**
     * 检查地形是否适合放置树木
     */
    isValidTerrainForTrees(x, z, currentHeight) {
        // 检查周围地形的坡度
        const sampleRadius = 2;
        const samples = [
            this.getHeightAtPosition(x + sampleRadius, z),
            this.getHeightAtPosition(x - sampleRadius, z),
            this.getHeightAtPosition(x, z + sampleRadius),
            this.getHeightAtPosition(x, z - sampleRadius)
        ];

        // 计算平均坡度
        let totalSlopeDiff = 0;
        for (const height of samples) {
            totalSlopeDiff += Math.abs(height - currentHeight);
        }
        const averageSlope = totalSlopeDiff / samples.length;

        // 坡度太陡或太低的地方不适合树木
        return averageSlope < 3 && currentHeight > 0.8;
    }

    /**
     * 获取地形复杂度
     */
    getTerrainComplexity(x, z) {
        const noise1 = Math.abs(this.noise2D(x * 0.02, z * 0.02));
        const noise2 = Math.abs(this.noise2D(x * 0.05, z * 0.05));
        return (noise1 + noise2) * 0.5;
    }

    /**
     * 创建单棵树
     */
    createTree() {
        const treeGroup = new THREE.Group();

        // 树干
        const trunkGeometry = new THREE.CylinderGeometry(0.3, 0.5, 4, 8);
        const trunkMaterial = new THREE.MeshStandardMaterial({
            color: 0x8B4513,
            roughness: 0.9,
            metalness: 0.0
        });
        const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
        trunk.position.y = 2;
        trunk.castShadow = true;
        treeGroup.add(trunk);

        // 树冠
        const crownGeometry = new THREE.SphereGeometry(2.5, 8, 6);
        const crownMaterial = new THREE.MeshStandardMaterial({
            color: 0x228B22,
            roughness: 0.8,
            metalness: 0.0
        });
        const crown = new THREE.Mesh(crownGeometry, crownMaterial);
        crown.position.y = 5;
        crown.scale.y = 0.8;
        crown.castShadow = true;
        treeGroup.add(crown);

        return treeGroup;
    }

    /**
     * 添加灌木 - 基于地形的有机分布
     */
    addBushes(group) {
        const bushCount = 30; // 再降灌木数量

        for (let i = 0; i < bushCount; i++) {
            // 有机分布，覆盖整个可能的岛屿区域
            const x = (Math.random() - 0.5) * this.islandRadius * 2.0;
            const z = (Math.random() - 0.5) * this.islandRadius * 2.0;
            const y = this.getHeightAtPosition(x, z);

            // 根据地形条件决定灌木类型和放置
            if (y > 0.1 && this.isValidTerrainForBushes(x, z, y)) {
                const bush = this.createBush();
                bush.position.set(x, y, z);

                // 根据地形调整灌木特征
                bush.rotation.y = Math.random() * Math.PI * 2;
                const terrainFactor = this.getTerrainComplexity(x, z);
                const heightFactor = Math.min(1, y / 5);
                const scale = 0.6 + terrainFactor * 0.4 + heightFactor * 0.3;
                bush.scale.setScalar(scale);

                group.add(bush);
            }
        }

        // 添加小草丛
        this.addGrassPatches(group);
    }

    /**
     * 检查地形是否适合放置灌木
     */
    isValidTerrainForBushes(x, z, currentHeight) {
        // 灌木比树木更适应各种地形
        const sampleRadius = 1;
        const samples = [
            this.getHeightAtPosition(x + sampleRadius, z),
            this.getHeightAtPosition(x - sampleRadius, z),
            this.getHeightAtPosition(x, z + sampleRadius),
            this.getHeightAtPosition(x, z - sampleRadius)
        ];

        let totalSlopeDiff = 0;
        for (const height of samples) {
            totalSlopeDiff += Math.abs(height - currentHeight);
        }
        const averageSlope = totalSlopeDiff / samples.length;

        // 灌木可以在更陡的坡度上生长
        return averageSlope < 5 && currentHeight > 0.05;
    }

    /**
     * 创建灌木
     */
    createBush() {
        const bushGeometry = new THREE.SphereGeometry(0.8, 6, 4);
        const bushMaterial = new THREE.MeshStandardMaterial({
            color: 0x32CD32,
            roughness: 0.7,
            metalness: 0.0
        });
        const bush = new THREE.Mesh(bushGeometry, bushMaterial);
        bush.scale.y = 0.6;
        bush.position.y = 0.4;
        bush.castShadow = true;
        return bush;
    }

    /**
     * 添加小草丛 - 有机分布模式
     */
    addGrassPatches(group) {
        const grassCount = 50;  // 大幅降低草丛密度

        for (let i = 0; i < grassCount; i++) {
            // 使用更广阔的有机分布
            const x = (Math.random() - 0.5) * this.islandRadius * 2.2;
            const z = (Math.random() - 0.5) * this.islandRadius * 2.2;
            const y = this.getHeightAtPosition(x, z);

            // 草丛可以在几乎所有地形上生长
            if (y > 0.02 && y < 8 && this.isValidTerrainForGrass(x, z, y)) {
                const grass = this.createGrassPatch();
                grass.position.set(x, y + 0.05, z);
                grass.rotation.y = Math.random() * Math.PI * 2;

                // 根据地形调整草丛密度和高度
                const terrainFactor = this.getTerrainComplexity(x, z);
                const heightFactor = Math.max(0.3, 1 - y / 10); // 高处草更稀疏
                grass.scale.setScalar(0.5 + terrainFactor * 0.3 + heightFactor * 0.4);

                group.add(grass);
            }
        }
    }

    /**
     * 检查地形是否适合放置草丛
     */
    isValidTerrainForGrass(x, z, currentHeight) {
        // 草丛几乎可以在任何地方生长，除了极陡的坡
        const sampleRadius = 0.5;
        const samples = [
            this.getHeightAtPosition(x + sampleRadius, z),
            this.getHeightAtPosition(x - sampleRadius, z),
            this.getHeightAtPosition(x, z + sampleRadius),
            this.getHeightAtPosition(x, z - sampleRadius)
        ];

        let totalSlopeDiff = 0;
        for (const height of samples) {
            totalSlopeDiff += Math.abs(height - currentHeight);
        }
        const averageSlope = totalSlopeDiff / samples.length;

        return averageSlope < 8; // 草丣容忍度最高
    }

    /**
     * 创建草丛
     */
    createGrassPatch() {
        const grassGroup = new THREE.Group();

        // 创建多根草叶
        for (let i = 0; i < 5; i++) {
            const grassGeometry = new THREE.PlaneGeometry(0.3, 0.8);
            const grassMaterial = new THREE.MeshStandardMaterial({
                color: new THREE.Color().setHSL(0.25 + Math.random() * 0.1, 0.6, 0.4),
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.8,
                roughness: 0.9,
                metalness: 0.0
            });

            const grass = new THREE.Mesh(grassGeometry, grassMaterial);
            grass.position.x = (Math.random() - 0.5) * 0.3;
            grass.position.z = (Math.random() - 0.5) * 0.3;
            grass.rotation.y = Math.random() * Math.PI * 2;
            grass.rotation.z = (Math.random() - 0.5) * 0.2; // 轻微倾斜

            grassGroup.add(grass);
        }

        const scale = 0.5 + Math.random() * 0.5;
        grassGroup.scale.setScalar(scale);

        return grassGroup;
    }

    /**
     * 添加棕榈树 - 沿海岸线有机分布
     */
    addPalmTrees(group) {
        const palmCount = 12; // 根据缩小比例减少棕榈树数量

        for (let i = 0; i < palmCount; i++) {
            // 在更大范围内搜索海岸线位置
            const x = (Math.random() - 0.5) * this.islandRadius * 2.5;
            const z = (Math.random() - 0.5) * this.islandRadius * 2.5;
            const y = this.getHeightAtPosition(x, z);

            // 棕榈树喜欢海滩和低地
            if (y > 0.05 && y < 2 && this.isNearCoastline(x, z, y)) {
                const palm = this.createPalmTree();
                palm.position.set(x, y, z);
                palm.rotation.y = Math.random() * Math.PI * 2;

                // 根据距离海岸的远近调整大小
                const coastDistance = this.getDistanceToCoast(x, z);
                const scale = 0.8 + Math.max(0, 1 - coastDistance / 10) * 0.4;
                palm.scale.setScalar(scale);

                group.add(palm);
            }
        }
    }

    /**
     * 检查是否靠近海岸线
     */
    isNearCoastline(x, z, currentHeight) {
        // 检查周围是否有水域（高度为0的区域）
        const checkRadius = 5;
        for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
            const checkX = x + Math.cos(angle) * checkRadius;
            const checkZ = z + Math.sin(angle) * checkRadius;
            const checkHeight = this.getHeightAtPosition(checkX, checkZ);

            if (checkHeight <= 0.1) {
                return true; // 找到了水域，说明靠近海岸
            }
        }
        return false;
    }

    /**
     * 计算到海岸的距离
     */
    getDistanceToCoast(x, z) {
        let minDistance = Infinity;
        const searchRadius = 20;
        const steps = 16;

        for (let i = 0; i < steps; i++) {
            const angle = (i / steps) * Math.PI * 2;
            for (let r = 1; r < searchRadius; r++) {
                const checkX = x + Math.cos(angle) * r;
                const checkZ = z + Math.sin(angle) * r;
                const height = this.getHeightAtPosition(checkX, checkZ);

                if (height <= 0.1) {
                    minDistance = Math.min(minDistance, r);
                    break;
                }
            }
        }

        return minDistance === Infinity ? searchRadius : minDistance;
    }

    /**
     * 创建棕榈树
     */
    createPalmTree() {
        const palmGroup = new THREE.Group();

        // 树干 - 弯曲的
        const trunkGeometry = new THREE.CylinderGeometry(0.2, 0.3, 6, 8);
        const trunkMaterial = new THREE.MeshStandardMaterial({
            color: 0xCD853F,
            roughness: 0.9,
            metalness: 0.0
        });
        const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
        trunk.position.y = 3;
        trunk.rotation.z = Math.random() * 0.3 - 0.15; // 轻微倾斜
        trunk.castShadow = true;
        palmGroup.add(trunk);

        // 棕榈叶
        for (let i = 0; i < 6; i++) {
            const leafGeometry = new THREE.PlaneGeometry(4, 1);
            const leafMaterial = new THREE.MeshStandardMaterial({
                color: 0x228B22,
                side: THREE.DoubleSide,
                roughness: 0.8,
                metalness: 0.0
            });
            const leaf = new THREE.Mesh(leafGeometry, leafMaterial);
            leaf.position.set(0, 6, 0);
            leaf.rotation.y = (i / 6) * Math.PI * 2;
            leaf.rotation.x = -0.3;
            leaf.castShadow = true;
            palmGroup.add(leaf);
        }

        return palmGroup;
    }

    /**
     * 创建海滩细节 - 增加更多装饰
     */
    createBeachDetails() {
        const beachGroup = new THREE.Group();

        // 添加贝壳、小石头和漂流木
        for (let i = 0; i < 60; i++) { // 降低海滩装饰密度，更卡通
            const angle = Math.random() * Math.PI * 2;
            const distance = this.islandRadius * (0.8 + Math.random() * 0.15);

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y < 0.8) {
                const rand = Math.random();
                if (rand < 0.4) {
                    const shell = this.createShell();
                    shell.position.set(x, y + 0.05, z);
                    shell.rotation.y = Math.random() * Math.PI * 2;
                    beachGroup.add(shell);
                } else if (rand < 0.8) {
                    const pebble = this.createPebble();
                    pebble.position.set(x, y + 0.02, z);
                    beachGroup.add(pebble);
                } else {
                    const driftwood = this.createDriftwood();
                    driftwood.position.set(x, y + 0.1, z);
                    driftwood.rotation.y = Math.random() * Math.PI * 2;
                    beachGroup.add(driftwood);
                }
            }
        }

        // 添加海藻
        this.addSeaweed(beachGroup);

        return beachGroup;
    }

    /**
     * 创建贝壳
     */
    createShell() {
        const shellGeometry = new THREE.SphereGeometry(0.1, 6, 4);
        const shellMaterial = new THREE.MeshStandardMaterial({
            color: 0xFFF8DC,
            roughness: 0.3,
            metalness: 0.0
        });
        const shell = new THREE.Mesh(shellGeometry, shellMaterial);
        shell.scale.y = 0.3;
        return shell;
    }

    /**
     * 创建小石头
     */
    createPebble() {
        const pebbleGeometry = new THREE.SphereGeometry(0.05, 6, 4);
        const pebbleMaterial = new THREE.MeshStandardMaterial({
            color: 0x696969,
            roughness: 0.8,
            metalness: 0.1
        });
        const pebble = new THREE.Mesh(pebbleGeometry, pebbleMaterial);
        pebble.scale.set(
            1 + Math.random() * 0.5,
            0.5 + Math.random() * 0.3,
            1 + Math.random() * 0.5
        );
        return pebble;
    }

    /**
     * 创建漂流木
     */
    createDriftwood() {
        const woodGeometry = new THREE.CylinderGeometry(0.08, 0.12, 1.5, 8);
        const woodMaterial = new THREE.MeshStandardMaterial({
            color: 0x8B6914,
            roughness: 0.95,
            metalness: 0.0
        });
        const wood = new THREE.Mesh(woodGeometry, woodMaterial);
        wood.rotation.z = Math.PI / 2; // 水平放置
        wood.rotation.y = (Math.random() - 0.5) * 0.3; // 轻微旋转
        return wood;
    }

    /**
     * 添加海藻
     */
    addSeaweed(group) {
        const seaweedCount = 20; // 降低海藻密度

        for (let i = 0; i < seaweedCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = this.islandRadius * (0.88 + Math.random() * 0.08);

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y < 0.3) {
                const seaweed = this.createSeaweed();
                seaweed.position.set(x, y + 0.1, z);
                group.add(seaweed);
            }
        }
    }

    /**
     * 创建海藻
     */
    createSeaweed() {
        const seaweedGeometry = new THREE.PlaneGeometry(0.5, 1.2);
        const seaweedMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color().setHSL(0.15, 0.7, 0.2),
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.8,
            roughness: 0.8,
            metalness: 0.0
        });
        const seaweed = new THREE.Mesh(seaweedGeometry, seaweedMaterial);
        seaweed.rotation.y = Math.random() * Math.PI * 2;
        seaweed.rotation.x = (Math.random() - 0.5) * 0.3;
        return seaweed;
    }

    /**
     * 创建岩石 - 增加更多类型和细节
     */
    createRocks() {
        const rockGroup = new THREE.Group();
        const rockCount = 15; // 减少岩石密度，画面更简洁

        for (let i = 0; i < rockCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * this.islandRadius * 0.8;

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y > 1.5) { // 在较高的地方放岩石
                const rockType = Math.floor(Math.random() * 3);
                let rock;

                switch (rockType) {
                    case 0:
                        rock = this.createRock();
                        break;
                    case 1:
                        rock = this.createBoulder();
                        break;
                    case 2:
                        rock = this.createRockFormation();
                        break;
                }

                rock.position.set(x, y, z);
                rockGroup.add(rock);
            }
        }

        return rockGroup;
    }

    /**
     * 创建岩石
     */
    createRock() {
        const rockGeometry = new THREE.DodecahedronGeometry(1, 0);
        const rockMaterial = new THREE.MeshStandardMaterial({
            color: 0x708090,
            roughness: 0.9,
            metalness: 0.2
        });
        const rock = new THREE.Mesh(rockGeometry, rockMaterial);

        // 随机缩放和旋转
        const scale = 0.5 + Math.random() * 1.5;
        rock.scale.set(scale, scale * 0.7, scale);
        rock.rotation.set(
            Math.random() * Math.PI,
            Math.random() * Math.PI,
            Math.random() * Math.PI
        );

        rock.castShadow = true;
        rock.receiveShadow = true;

        return rock;
    }

    /**
     * 创建大石头
     */
    createBoulder() {
        const boulderGeometry = new THREE.SphereGeometry(1.5, 8, 6);
        const boulderMaterial = new THREE.MeshStandardMaterial({
            color: 0x696969,
            roughness: 0.95,
            metalness: 0.1
        });
        const boulder = new THREE.Mesh(boulderGeometry, boulderMaterial);

        // 压扁一点
        boulder.scale.set(1, 0.7, 1.2);
        boulder.rotation.set(
            Math.random() * Math.PI,
            Math.random() * Math.PI,
            Math.random() * Math.PI
        );

        return boulder;
    }

    /**
     * 创建岩石群
     */
    createRockFormation() {
        const formationGroup = new THREE.Group();

        // 创建2-4个小岩石组合
        const rockCount = 2 + Math.floor(Math.random() * 3);
        for (let i = 0; i < rockCount; i++) {
            const smallRock = this.createRock();
            smallRock.scale.setScalar(0.5 + Math.random() * 0.5);
            smallRock.position.set(
                (Math.random() - 0.5) * 2,
                0,
                (Math.random() - 0.5) * 2
            );
            formationGroup.add(smallRock);
        }

        return formationGroup;
    }

    /**
     * 计算真正不规则的海岛高度 - 突破圆形限制
     */
    calculateIrregularHeight(x, z) {
        // 1. 创建复杂的有机形状而非圆形
        const angle = Math.atan2(z, x);
        const distance = Math.sqrt(x * x + z * z);

        // 多层复合海岸线变化
        const coastVariation1 = Math.sin(angle * this.coastComplexity) * this.shapeVariation;
        const coastVariation2 = Math.sin(angle * this.coastComplexity * 0.7 + 1.5) * this.shapeVariation * 0.6;
        const coastVariation3 = Math.sin(angle * this.coastComplexity * 1.3 + 3.1) * this.shapeVariation * 0.4;

        // 有机破碎化 - 创造半岛和海湾（增强版）
        const fragmentNoise1 = this.noise2D(x * 0.01, z * 0.01);
        const fragmentNoise2 = this.noise2D(x * 0.02, z * 0.02) * 0.5;
        const fragmentNoise3 = this.noise2D(x * 0.005, z * 0.005) * 0.3;
        const combinedFragmentNoise = fragmentNoise1 + fragmentNoise2 + fragmentNoise3;
        
        const organicShape = 1 + (coastVariation1 + coastVariation2 + coastVariation3) +
                           combinedFragmentNoise * this.fragmentationLevel;

        // 基础半径根据角度和噪声极大变化，允许更极端的形状
        const irregularRadius = this.islandRadius * Math.max(0.2, organicShape * this.organicFactor);

        // 2. 多层噪声组合 - 更强的地形变化
        const primaryHeight = this.noise2D(x * this.primaryNoise, z * this.primaryNoise);
        const detailHeight = this.noise2D(x * this.detailNoise, z * this.detailNoise) * 0.2; // 降低细节影响
        const coastNoise = this.noise2D(x * this.coastNoise, z * this.coastNoise) * 0.2;   // 弱化海岸噪声

        // 添加更多细节噪声层
        const microDetail = this.noise2D(x * 0.1, z * 0.1) * 0.08;   // 进一步降低高频杂波
        const edgeDetail = this.noise2D(x * 0.05, z * 0.05) * 0.12;  // 减少边沿锯齿

        // 3. 组合所有噪声
        const combinedNoise = primaryHeight + detailHeight + coastNoise + microDetail + edgeDetail;

        // 4. 动态衰减 - 根据有机形状调整
        const normalizedDistance = distance / irregularRadius;

        // 更复杂的衰减函数，创造更自然的边界
        let falloff;
        if (normalizedDistance < 0.6) {
            falloff = 1.0; // 内部区域保持完整高度
        } else if (normalizedDistance < 0.85) {
            // 中间区域渐变
            const t = (normalizedDistance - 0.6) / 0.25;
            falloff = 1 - Math.pow(t, 1.5);
        } else {
            // 边缘区域快速衰减
            const t = Math.min((normalizedDistance - 0.85) / 0.3, 1);
            falloff = Math.pow(1 - t, 2);
        }

        // 5. 添加地形特征（进一步减弱山脊/山谷的影响，避免尖锐）
        const ridges = this.createRidges(x, z) * 0.15;
        const valleys = this.createValleys(x, z) * 0.12;

        // 6. 最终高度计算
        let finalHeight = (combinedNoise + ridges - valleys) * this.heightScale * falloff;

        // Cartoon 压缩：压低山体，低地更平缓（加强）
        finalHeight = this.cartoonizeHeight(finalHeight);

        // 7. 动态海滩平台 - 根据有机形状调整
        const beachZoneStart = 0.75 + fragmentNoise * 0.1;
        const beachZoneEnd = 0.92 + fragmentNoise * 0.05;

        if (normalizedDistance > beachZoneStart && normalizedDistance < beachZoneEnd && finalHeight > 0) {
            const beachHeight = 0.3 + Math.abs(fragmentNoise) * 0.4;
            finalHeight = Math.max(finalHeight, beachHeight);
        }

        // 8. 确保岛屿有真正的边界变化
        const edgeFactor = this.calculateEdgeFactor(x, z);
        finalHeight *= edgeFactor;

        return Math.max(0, finalHeight);
    }

    /**
     * 计算边缘因子 - 创造极其不规则的岛屿边界
     */
    calculateEdgeFactor(x, z) {
        const angle = Math.atan2(z, x);
        const distance = Math.sqrt(x * x + z * z);

        // 创建极其复杂的边界形状，大幅增加变化
        const shape1 = Math.sin(angle * 11 + 0.5) * 0.8;  // 大幅增加变化幅度
        const shape2 = Math.sin(angle * 17 + 2.1) * 0.7;  // 大幅增加变化幅度
        const shape3 = Math.sin(angle * 23 + 4.3) * 0.6; // 大幅增加频率和幅度
        const shape4 = Math.sin(angle * 31 + 1.7) * 0.5; // 极高频的变化
        const shape5 = Math.sin(angle * 41 + 3.2) * 0.4; // 新增超高频变化

        // 多层噪声影响边界，创造极其自然的不规则性
        const noiseX = x * 0.015;
        const noiseZ = z * 0.015;
        const edgeNoise1 = this.noise2D(noiseX, noiseZ) * 1.2;        // 主要噪声 - 大幅增强
        const edgeNoise2 = this.noise2D(noiseX * 3, noiseZ * 3) * 0.8; // 细节噪声 - 增强
        const edgeNoise3 = this.noise2D(noiseX * 0.3, noiseZ * 0.3) * 1.0; // 大尺度变化 - 增强
        const edgeNoise4 = this.noise2D(noiseX * 8, noiseZ * 8) * 0.6; // 新增超细节噪声

        // 距离相关的变化 - 让不同半径有不同的变化模式
        const distanceVariation = Math.sin(distance * 0.1) * 0.5;

        // 组合所有形状因素，创造极强的不规则性
        const combinedShape = 1 + shape1 + shape2 + shape3 + shape4 + shape5 + 
                             edgeNoise1 + edgeNoise2 + edgeNoise3 + edgeNoise4 + distanceVariation;

        // 允许极大的变化范围，创造极其明显的海湾和半岛
        // 完全移除上限，允许极端的形状变化
        return Math.max(0.1, combinedShape); // 只保留最小值，移除最大值限制
    }

    /**
     * 检测是否为沙滩区域 - 基于地形分析
     */
    isBeachArea(x, z, height) {
        // 1. 沙滩可以是较低海拔区域（放宽限制）
        if (height > 2.0) {
            return false;
        }

        // 2. 检查周围是否有较高的陆地
        const sampleRadius = 3.0; // 增大采样范围
        const samplePoints = 8;
        let nearbyHighGround = 0;

        for (let i = 0; i < samplePoints; i++) {
            const angle = (i / samplePoints) * Math.PI * 2;
            const sampleX = x + Math.cos(angle) * sampleRadius;
            const sampleZ = z + Math.sin(angle) * sampleRadius;
            const sampleHeight = this.calculateIrregularHeight(sampleX, sampleZ);

            if (sampleHeight > 1.5) { // 降低高地判断阈值
                nearbyHighGround++;
            }
        }

        // 3. 只需要至少一个附近高地就算沙滩（放宽条件）
        return nearbyHighGround >= 1;
    }

    /**
     * 计算不规则沙滩高度 - 基于地形轮廓而非圆形边界
     */
    calculateBeachHeight(x, z) {
        // 1. 首先计算实际的地形高度
        const terrainHeight = this.calculateIrregularHeight(x, z);

        // 2. 如果地形高度已经足够高，不是沙滩区域
        if (terrainHeight > 3.0) {
            return 0; // 不生成沙滩
        }

        // 3. 采样周围地形高度，确定是否在地形边缘
        const sampleRadius = 2.5; // 增大采样范围
        const samplePoints = 8;
        let landNearby = false;
        let highGroundCount = 0;

        for (let i = 0; i < samplePoints; i++) {
            const angle = (i / samplePoints) * Math.PI * 2;
            const sampleX = x + Math.cos(angle) * sampleRadius;
            const sampleZ = z + Math.sin(angle) * sampleRadius;
            const sampleHeight = this.calculateIrregularHeight(sampleX, sampleZ);

            if (sampleHeight > 1.0) { // 降低高地判断阈值
                landNearby = true;
                highGroundCount++;
            }
        }

        // 4. 只有在靠近陆地的地方才生成沙滩（放宽条件）
        if (!landNearby) {
            return 0; // 不在陆地边缘，不生成沙滩
        }

        // 5. 计算沙滩高度梯度 - 基于到最近高地的距离
        const gradientRadius = 6.0; // 增大梯度范围
        let nearestHighGroundDistance = gradientRadius;

        // 寻找最近的高地
        for (let angle = 0; angle < Math.PI * 2; angle += 0.2) {
            for (let radius = 0.5; radius < gradientRadius; radius += 0.5) {
                const testX = x + Math.cos(angle) * radius;
                const testZ = z + Math.sin(angle) * radius;
                const testHeight = this.calculateIrregularHeight(testX, testZ);

                if (testHeight > 2.0) { // 降低高地判断阈值
                    nearestHighGroundDistance = Math.min(nearestHighGroundDistance, radius);
                    break;
                }
            }
        }

        // 6. 基于到高地的距离计算沙滩高度（大幅增加高度）
        const distanceFactor = nearestHighGroundDistance / gradientRadius;
        const baseBeachHeight = Math.max(0, (1 - distanceFactor) * 1.5); // 从0.25增加到1.5

        // 7. 添加沙滩地形变化
        const beachNoise = this.noise2D(x * 0.1, z * 0.1) * 0.2; // 增加噪声变化
        const fineDetail = this.noise2D(x * 0.3, z * 0.3) * 0.1;

        // 8. 确保沙滩有最小高度，让它更明显
        const finalBeachHeight = Math.max(0.3, baseBeachHeight + beachNoise + fineDetail);
        return Math.min(finalBeachHeight, Math.max(terrainHeight, 0.3) + 0.2);
    }

    /**
     * 创建山脊
     */
    createRidges(x, z) {
        const ridgeNoise1 = Math.abs(this.noise2D(x * 0.015, z * 0.015));
        const ridgeNoise2 = Math.abs(this.noise2D(x * 0.025, z * 0.025));
        return Math.pow(ridgeNoise1, 3) * 0.5 + Math.pow(ridgeNoise2, 2) * 0.3;
    }

    /**
     * 创建山谷
     */
    createValleys(x, z) {
        const valleyNoise1 = this.noise2D(x * 0.01, z * 0.01);
        const valleyNoise2 = this.noise2D(x * 0.03, z * 0.03);
        return Math.max(0, valleyNoise1 * valleyNoise2) * 0.4;
    }

    /**
     * 获取指定位置的高度 (保持兼容性)
     */
    getHeightAtPosition(x, z) {
        return this.calculateIrregularHeight(x, z);
    }

    /**
     * 简单的2D噪声函数
     */
    noise2D(x, y) {
        // 简化的Perlin噪声实现
        const X = Math.floor(x) & 255;
        const Y = Math.floor(y) & 255;

        x -= Math.floor(x);
        y -= Math.floor(y);

        const u = this.fade(x);
        const v = this.fade(y);

        const A = this.p[X] + Y;
        const B = this.p[X + 1] + Y;

        return this.lerp(v,
            this.lerp(u, this.grad(this.p[A], x, y), this.grad(this.p[B], x - 1, y)),
            this.lerp(u, this.grad(this.p[A + 1], x, y - 1), this.grad(this.p[B + 1], x - 1, y - 1))
        );
    }

    /**
     * 噪声辅助函数
     */
    fade(t) {
        return t * t * t * (t * (t * 6 - 15) + 10);
    }

    lerp(t, a, b) {
        return a + t * (b - a);
    }

    grad(hash, x, y) {
        const h = hash & 15;
        const u = h < 8 ? x : y;
        const v = h < 4 ? y : h === 12 || h === 14 ? x : 0;
        return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
    }

    // Perlin噪声排列表
    p = [
        151,160,137,91,90,15,131,13,201,95,96,53,194,233,7,225,140,36,103,30,69,142,8,99,37,240,21,10,23,
        190,6,148,247,120,234,75,0,26,197,62,94,252,219,203,117,35,11,32,57,177,33,88,237,149,56,87,174,20,125,136,171,168,68,175,74,165,71,134,139,48,27,166,
        77,146,158,231,83,111,229,122,60,211,133,230,220,105,92,41,55,46,245,40,244,102,143,54,65,25,63,161,1,216,80,73,209,76,132,187,208,89,18,169,200,196,
        135,130,116,188,159,86,164,100,109,198,173,186,3,64,52,217,226,250,124,123,5,202,38,147,118,126,255,82,85,212,207,206,59,227,47,16,58,17,182,189,28,42,
        223,183,170,213,119,248,152,2,44,154,163,70,221,153,101,155,167,43,172,9,129,22,39,253,19,98,108,110,79,113,224,232,178,185,112,104,218,246,97,228,
        251,34,242,193,238,210,144,12,191,179,162,241,81,51,145,235,249,14,239,107,49,192,214,31,181,199,106,157,184,84,204,176,115,121,50,45,127,4,150,254,
        138,236,205,93,222,114,67,29,24,72,243,141,128,195,78,66,215,61,156,180
    ];

    /**
     * 导出海岛为 GLB 文件
     * @param {THREE.Group} islandGroup - 海岛模型组
     * @param {string} filename - 文件名（不带扩展名）
     */
    async exportToGLB(islandGroup, filename = 'procedural-island') {
        const exporter = new GLTFExporter();

        try {
            console.log(`🔄 开始导出海岛模型: ${filename}.glb`);

            // 导出选项
            const options = {
                binary: true,          // 导出为二进制 GLB 格式
                embedImages: true,     // 嵌入贴图
                includeCustomExtensions: false,
                onlyVisible: true,     // 只导出可见对象
                truncateDrawRange: true,
                maxTextureSize: 1024   // 限制贴图大小
            };

            // 执行导出
            const result = await new Promise((resolve, reject) => {
                exporter.parse(
                    islandGroup,
                    (gltf) => resolve(gltf),
                    (error) => reject(error),
                    options
                );
            });

            // 创建下载链接
            const blob = new Blob([result], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);

            // 自动下载文件
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.glb`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // 清理 URL
            URL.revokeObjectURL(url);

            console.log(`✅ 成功导出海岛模型: ${filename}.glb`);
            return true;

        } catch (error) {
            console.error('❌ 导出 GLB 文件失败:', error);
            return false;
        }
    }

    /**
     * 创建微观细节 - 小花、蘑菇、昆虫、落叶等
     */
    createMicroDetails() {
        const microGroup = new THREE.Group();

        // 添加小花朵
        this.addWildflowers(microGroup);

        // 添加蘑菇
        this.addMushrooms(microGroup);

        // 添加蕨类植物
        this.addFerns(microGroup);

        // 添加藤蔓
        this.addVines(microGroup);

        // 添加落叶堆
        this.addLeafPiles(microGroup);

        // 添加小树枝和木块
        this.addFallenBranches(microGroup);

        return microGroup;
    }

    /**
     * 添加野花 - 基于地形的自然分布
     */
    addWildflowers(group) {
        const flowerCount = 40; // 大幅降低花朵数量（更卡通）

        for (let i = 0; i < flowerCount; i++) {
            // 在整个地形范围内有机分布
            const x = (Math.random() - 0.5) * this.islandRadius * 2.0;
            const z = (Math.random() - 0.5) * this.islandRadius * 2.0;
            const y = this.getHeightAtPosition(x, z);

            // 花朵喜欢阳光充足的开阔地带
            if (y > 0.3 && y < 6 && this.isValidTerrainForFlowers(x, z, y)) {
                const flower = this.createWildflower();
                flower.position.set(x, y + 0.05, z);
                flower.rotation.y = Math.random() * Math.PI * 2;

                // 根据地形调整花朵大小和颜色密度
                const terrainFactor = this.getTerrainComplexity(x, z);
                const scale = 0.7 + terrainFactor * 0.5;
                flower.scale.setScalar(scale);

                group.add(flower);
            }
        }
    }

    /**
     * 检查地形是否适合花朵生长
     */
    isValidTerrainForFlowers(x, z, currentHeight) {
        // 花朵需要相对平坦的地面和适中的海拔
        const sampleRadius = 1;
        const samples = [
            this.getHeightAtPosition(x + sampleRadius, z),
            this.getHeightAtPosition(x - sampleRadius, z),
            this.getHeightAtPosition(x, z + sampleRadius),
            this.getHeightAtPosition(x, z - sampleRadius)
        ];

        let totalSlopeDiff = 0;
        for (const height of samples) {
            totalSlopeDiff += Math.abs(height - currentHeight);
        }
        const averageSlope = totalSlopeDiff / samples.length;

        // 不要在过于陡峭或过于茂密的林区
        return averageSlope < 2.5 && !this.isInDenseForest(x, z);
    }

    /**
     * 检查是否在茂密森林中
     */
    isInDenseForest(x, z) {
        // 简单检查：如果周围有多个树木样本点，认为是茂密森林
        const forestNoise = this.noise2D(x * 0.03, z * 0.03);
        return forestNoise > 0.3;
    }

    /**
     * 创建野花
     */
    createWildflower() {
        const flowerGroup = new THREE.Group();

        // 花茎
        const stemGeometry = new THREE.CylinderGeometry(0.01, 0.02, 0.3, 4);
        const stemMaterial = new THREE.MeshStandardMaterial({
            color: 0x228B22,
            roughness: 0.8,
            metalness: 0.0
        });
        const stem = new THREE.Mesh(stemGeometry, stemMaterial);
        stem.position.y = 0.15;
        flowerGroup.add(stem);

        // 花朵
        const flowerGeometry = new THREE.SphereGeometry(0.08, 6, 4);
        const flowerColors = [0xFF69B4, 0xFF6347, 0xFFFF00, 0x9370DB, 0xFFA500];
        const flowerMaterial = new THREE.MeshStandardMaterial({
            color: flowerColors[Math.floor(Math.random() * flowerColors.length)],
            roughness: 0.3,
            metalness: 0.0
        });
        const flower = new THREE.Mesh(flowerGeometry, flowerMaterial);
        flower.position.y = 0.28;
        flower.scale.y = 0.5;
        flowerGroup.add(flower);

        const scale = 0.7 + Math.random() * 0.6;
        flowerGroup.scale.setScalar(scale);

        return flowerGroup;
    }

    /**
     * 添加蘑菇
     */
    addMushrooms(group) {
        const mushroomCount = 18; // 降低蘑菇密度

        for (let i = 0; i < mushroomCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * this.islandRadius * 0.7;

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y > 0.5 && y < 6) { // 在湿润的中等高度
                const mushroom = this.createMushroom();
                mushroom.position.set(x, y + 0.02, z);
                group.add(mushroom);
            }
        }
    }

    /**
     * 创建蘑菇
     */
    createMushroom() {
        const mushroomGroup = new THREE.Group();

        // 蘑菇茎
        const stemGeometry = new THREE.CylinderGeometry(0.05, 0.08, 0.4, 6);
        const stemMaterial = new THREE.MeshStandardMaterial({
            color: 0xF5F5DC,
            roughness: 0.9,
            metalness: 0.0
        });
        const stem = new THREE.Mesh(stemGeometry, stemMaterial);
        stem.position.y = 0.2;
        mushroomGroup.add(stem);

        // 蘑菇帽
        const capGeometry = new THREE.SphereGeometry(0.2, 8, 6);
        const capColors = [0x8B4513, 0xA0522D, 0xCD853F, 0xFF6347];
        const capMaterial = new THREE.MeshStandardMaterial({
            color: capColors[Math.floor(Math.random() * capColors.length)],
            roughness: 0.7,
            metalness: 0.0
        });
        const cap = new THREE.Mesh(capGeometry, capMaterial);
        cap.position.y = 0.35;
        cap.scale.y = 0.6;
        mushroomGroup.add(cap);

        const scale = 0.5 + Math.random() * 0.8;
        mushroomGroup.scale.setScalar(scale);

        return mushroomGroup;
    }

    /**
     * 添加蕨类植物
     */
    addFerns(group) {
        const fernCount = 22; // 降低蕨类植物密度

        for (let i = 0; i < fernCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * this.islandRadius * 0.75;

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y > 0.3 && y < 5) { // 在阴湿环境
                const fern = this.createFern();
                fern.position.set(x, y + 0.1, z);
                fern.rotation.y = Math.random() * Math.PI * 2;
                group.add(fern);
            }
        }
    }

    /**
     * 创建蕨类植物
     */
    createFern() {
        const fernGroup = new THREE.Group();

        // 创建多片蕨叶
        for (let i = 0; i < 4; i++) {
            const leafGeometry = new THREE.PlaneGeometry(0.8, 1.5);
            const leafMaterial = new THREE.MeshStandardMaterial({
                color: new THREE.Color().setHSL(0.25, 0.8, 0.3),
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.9,
                roughness: 0.8,
                metalness: 0.0
            });

            const leaf = new THREE.Mesh(leafGeometry, leafMaterial);
            leaf.position.y = 0.7;
            leaf.rotation.y = (i / 4) * Math.PI * 2 + Math.random() * 0.5;
            leaf.rotation.z = (Math.random() - 0.5) * 0.3;

            // 添加叶片的锯齿效果
            const vertices = leaf.geometry.attributes.position.array;
            for (let j = 0; j < vertices.length; j += 9) {
                vertices[j] += (Math.random() - 0.5) * 0.1; // X坐标轻微变化
            }
            leaf.geometry.attributes.position.needsUpdate = true;

            fernGroup.add(leaf);
        }

        const scale = 0.6 + Math.random() * 0.6;
        fernGroup.scale.setScalar(scale);

        return fernGroup;
    }

    /**
     * 添加藤蔓
     */
    addVines(group) {
        const vineCount = 12;

        for (let i = 0; i < vineCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * this.islandRadius * 0.6;

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y > 2) { // 在较高的地方添加垂挂藤蔓
                const vine = this.createVine();
                vine.position.set(x, y + 2, z);
                group.add(vine);
            }
        }
    }

    /**
     * 创建藤蔓
     */
    createVine() {
        const vineGroup = new THREE.Group();

        // 创建蜿蜒的藤蔓主体
        const points = [];
        const segments = 8;
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            points.push(new THREE.Vector3(
                Math.sin(t * Math.PI * 2) * 0.3,
                -t * 3,
                Math.cos(t * Math.PI * 2) * 0.2
            ));
        }

        const curve = new THREE.CatmullRomCurve3(points);
        const tubeGeometry = new THREE.TubeGeometry(curve, 20, 0.03, 4, false);
        const vineMaterial = new THREE.MeshStandardMaterial({
            color: 0x228B22,
            roughness: 0.9,
            metalness: 0.0
        });
        const vine = new THREE.Mesh(tubeGeometry, vineMaterial);
        vineGroup.add(vine);

        // 添加叶子
        for (let i = 0; i < 6; i++) {
            const leafGeometry = new THREE.PlaneGeometry(0.3, 0.5);
            const leafMaterial = new THREE.MeshStandardMaterial({
                color: 0x32CD32,
                side: THREE.DoubleSide,
                roughness: 0.8,
                metalness: 0.0
            });
            const leaf = new THREE.Mesh(leafGeometry, leafMaterial);

            const t = i / 6;
            const position = curve.getPoint(t);
            leaf.position.copy(position);
            leaf.rotation.set(
                Math.random() * 0.5,
                Math.random() * Math.PI * 2,
                Math.random() * 0.5
            );

            vineGroup.add(leaf);
        }

        return vineGroup;
    }

    /**
     * 添加落叶堆
     */
    addLeafPiles(group) {
        const pileCount = 40; // 增加落叶堆密度

        for (let i = 0; i < pileCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * this.islandRadius * 0.8;

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y > 0.5 && y < 7) {
                const pile = this.createLeafPile();
                pile.position.set(x, y + 0.05, z);
                group.add(pile);
            }
        }
    }

    /**
     * 创建落叶堆
     */
    createLeafPile() {
        const pileGroup = new THREE.Group();
        const leafCount = 8 + Math.floor(Math.random() * 8);

        for (let i = 0; i < leafCount; i++) {
            const leafGeometry = new THREE.PlaneGeometry(0.2, 0.3);
            const leafColors = [0xDEB887, 0xD2691E, 0xCD853F, 0xA0522D, 0x8B4513];
            const leafMaterial = new THREE.MeshStandardMaterial({
                color: leafColors[Math.floor(Math.random() * leafColors.length)],
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.8,
                roughness: 0.9,
                metalness: 0.0
            });

            const leaf = new THREE.Mesh(leafGeometry, leafMaterial);
            leaf.position.set(
                (Math.random() - 0.5) * 0.8,
                Math.random() * 0.2,
                (Math.random() - 0.5) * 0.8
            );
            leaf.rotation.set(
                (Math.random() - 0.5) * Math.PI,
                Math.random() * Math.PI * 2,
                (Math.random() - 0.5) * Math.PI
            );

            pileGroup.add(leaf);
        }

        return pileGroup;
    }

    /**
     * 添加掉落的树枝
     */
    addFallenBranches(group) {
        const branchCount = 30; // 增加掉落树枝密度

        for (let i = 0; i < branchCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * this.islandRadius * 0.8;

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y > 0.2 && y < 6) {
                const branch = this.createFallenBranch();
                branch.position.set(x, y + 0.05, z);
                branch.rotation.y = Math.random() * Math.PI * 2;
                group.add(branch);
            }
        }
    }

    /**
     * 创建掉落的树枝
     */
    createFallenBranch() {
        const branchGroup = new THREE.Group();

        // 主要树枝
        const mainGeometry = new THREE.CylinderGeometry(0.05, 0.08, 1.2, 6);
        const branchMaterial = new THREE.MeshStandardMaterial({
            color: 0x8B4513,
            roughness: 0.95,
            metalness: 0.0
        });
        const mainBranch = new THREE.Mesh(mainGeometry, branchMaterial);
        mainBranch.rotation.z = Math.PI / 2; // 水平放置
        mainBranch.position.y = 0.05;
        branchGroup.add(mainBranch);

        // 添加小分支
        for (let i = 0; i < 3; i++) {
            const smallGeometry = new THREE.CylinderGeometry(0.02, 0.03, 0.4, 4);
            const smallBranch = new THREE.Mesh(smallGeometry, branchMaterial);
            smallBranch.position.set(
                (Math.random() - 0.5) * 0.8,
                0.1,
                (Math.random() - 0.5) * 0.3
            );
            smallBranch.rotation.set(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            );
            branchGroup.add(smallBranch);
        }

        return branchGroup;
    }

    /**
     * 创建水边湿地植物
     */
    createWetlandPlants() {
        const wetlandGroup = new THREE.Group();

        // 添加芦苇
        this.addReeds(wetlandGroup);

        // 添加睡莲叶
        this.addLilyPads(wetlandGroup);

        // 添加湿地草
        this.addMarshGrass(wetlandGroup);

        // 添加水边灌木
        this.addShorelineBushes(wetlandGroup);

        return wetlandGroup;
    }

    /**
     * 添加芦苇
     */
    addReeds(group) {
        const reedCount = 35;

        for (let i = 0; i < reedCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = this.islandRadius * (0.88 + Math.random() * 0.08);

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y < 0.5) { // 在水边
                const reed = this.createReed();
                reed.position.set(x, y, z);
                reed.rotation.y = Math.random() * Math.PI * 2;
                group.add(reed);
            }
        }
    }

    /**
     * 创建芦苇
     */
    createReed() {
        const reedGroup = new THREE.Group();

        // 创建多根芦苇茎
        for (let i = 0; i < 3; i++) {
            const stemGeometry = new THREE.CylinderGeometry(0.02, 0.03, 2, 4);
            const stemMaterial = new THREE.MeshStandardMaterial({
                color: 0x6B8E23,
                roughness: 0.9,
                metalness: 0.0
            });
            const stem = new THREE.Mesh(stemGeometry, stemMaterial);
            stem.position.set(
                (Math.random() - 0.5) * 0.2,
                1,
                (Math.random() - 0.5) * 0.2
            );
            stem.rotation.z = (Math.random() - 0.5) * 0.2;
            reedGroup.add(stem);

            // 芦苇穗
            const headGeometry = new THREE.CylinderGeometry(0.05, 0.02, 0.3, 6);
            const headMaterial = new THREE.MeshStandardMaterial({
                color: 0xDEB887,
                roughness: 0.8,
                metalness: 0.0
            });
            const head = new THREE.Mesh(headGeometry, headMaterial);
            head.position.copy(stem.position);
            head.position.y = 2.1;
            reedGroup.add(head);
        }

        return reedGroup;
    }

    /**
     * 添加睡莲叶
     */
    addLilyPads(group) {
        const padCount = 20;

        for (let i = 0; i < padCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = this.islandRadius * (0.9 + Math.random() * 0.05);

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = -0.05; // 漂浮在水面上

            const pad = this.createLilyPad();
            pad.position.set(x, y, z);
            pad.rotation.y = Math.random() * Math.PI * 2;
            group.add(pad);
        }
    }

    /**
     * 创建睡莲叶
     */
    createLilyPad() {
        const padGeometry = new THREE.CircleGeometry(0.5, 8);
        const padMaterial = new THREE.MeshStandardMaterial({
            color: 0x228B22,
            side: THREE.DoubleSide,
            roughness: 0.8,
            metalness: 0.0
        });
        const pad = new THREE.Mesh(padGeometry, padMaterial);
        pad.rotation.x = -Math.PI / 2;

        // 添加睡莲叶的切口
        const vertices = pad.geometry.attributes.position.array;
        for (let i = 0; i < vertices.length; i += 3) {
            const x = vertices[i];
            const z = vertices[i + 1];
            const distance = Math.sqrt(x * x + z * z);

            if (distance > 0.3) {
                // 在边缘添加轻微的波浪效果
                vertices[i + 2] += Math.sin(Math.atan2(z, x) * 8) * 0.05;
            }
        }
        pad.geometry.attributes.position.needsUpdate = true;

        const scale = 0.6 + Math.random() * 0.8;
        pad.scale.setScalar(scale);

        return pad;
    }

    /**
     * 添加湿地草
     */
    addMarshGrass(group) {
        const grassCount = 50;

        for (let i = 0; i < grassCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = this.islandRadius * (0.85 + Math.random() * 0.1);

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y < 0.8) { // 在水边湿地
                const grass = this.createMarshGrass();
                grass.position.set(x, y, z);
                group.add(grass);
            }
        }
    }

    /**
     * 创建湿地草
     */
    createMarshGrass() {
        const grassGroup = new THREE.Group();

        // 创建多根湿地草叶
        for (let i = 0; i < 8; i++) {
            const leafGeometry = new THREE.PlaneGeometry(0.1, 1);
            const leafMaterial = new THREE.MeshStandardMaterial({
                color: new THREE.Color().setHSL(0.2, 0.7, 0.3),
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.9,
                roughness: 0.9,
                metalness: 0.0
            });

            const leaf = new THREE.Mesh(leafGeometry, leafMaterial);
            leaf.position.set(
                (Math.random() - 0.5) * 0.3,
                0.5,
                (Math.random() - 0.5) * 0.3
            );
            leaf.rotation.y = Math.random() * Math.PI * 2;
            leaf.rotation.z = (Math.random() - 0.5) * 0.3;

            grassGroup.add(leaf);
        }

        return grassGroup;
    }

    /**
     * 添加海岸线灌木
     */
    addShorelineBushes(group) {
        const bushCount = 25;

        for (let i = 0; i < bushCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = this.islandRadius * (0.82 + Math.random() * 0.08);

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const y = this.getHeightAtPosition(x, z);

            if (y > 0.2 && y < 1.5) { // 在海岸线附近
                const bush = this.createShorelineBush();
                bush.position.set(x, y, z);
                bush.rotation.y = Math.random() * Math.PI * 2;
                group.add(bush);
            }
        }
    }

    /**
     * 创建海岸线灌木
     */
    createShorelineBush() {
        const bushGroup = new THREE.Group();

        // 主要灌木体
        const bushGeometry = new THREE.SphereGeometry(0.6, 8, 6);
        const bushMaterial = new THREE.MeshStandardMaterial({
            color: 0x2E8B57,
            roughness: 0.8,
            metalness: 0.0
        });
        const bush = new THREE.Mesh(bushGeometry, bushMaterial);
        bush.scale.set(1, 0.7, 1.2);
        bush.position.y = 0.4;
        bushGroup.add(bush);

        // 添加小分支
        for (let i = 0; i < 4; i++) {
            const branchGeometry = new THREE.SphereGeometry(0.3, 6, 4);
            const branch = new THREE.Mesh(branchGeometry, bushMaterial);
            branch.position.set(
                (Math.random() - 0.5) * 0.8,
                0.3 + Math.random() * 0.4,
                (Math.random() - 0.5) * 0.8
            );
            branch.scale.setScalar(0.6 + Math.random() * 0.4);
            bushGroup.add(branch);
        }

        const scale = 0.7 + Math.random() * 0.6;
        bushGroup.scale.setScalar(scale);

        return bushGroup;
    }

    /**
     * 批量导出所有海岛为 GLB 文件
     * @param {Array} islandPositions - 海岛位置数组
     */
    async exportAllIslands(islandPositions = [
        { x: 70, z: 60, name: 'island-northeast' },
        { x: -80, z: 70, name: 'island-northwest' },
        { x: 60, z: -75, name: 'island-southeast' },
        { x: -65, z: -60, name: 'island-southwest' }
    ]) {
        console.log('🏝️ 开始批量导出海岛模型...');

        for (let i = 0; i < islandPositions.length; i++) {
            const pos = islandPositions[i];
            console.log(`📦 创建海岛 ${i + 1}/${islandPositions.length}: ${pos.name}`);

            // 创建新的海岛实例
            const island = this.createIsland({ x: 0, y: 0, z: 0 }); // 中心位置创建，方便导出

            // 导出到 GLB
            await this.exportToGLB(island, pos.name);

            // 短暂延迟避免浏览器阻塞
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        console.log('✅ 所有海岛模型导出完成！');
    }

    /**
     * 几何体平滑处理 - 学习船只的平滑效果
     */
    smoothGeometry(geometry) {
        const positions = geometry.attributes.position.array;

        // 创建平滑后的位置数组
        const smoothedPositions = new Float32Array(positions.length);

        // 复制原始数据
        for (let i = 0; i < positions.length; i++) {
            smoothedPositions[i] = positions[i];
        }

        // 多次拉普拉斯平滑处理（进一步加强强度，尤其平滑山体）
        for (let iteration = 0; iteration < 8; iteration++) {
            this.laplacianSmooth(smoothedPositions, geometry);
        }

        // 更新几何体位置
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(smoothedPositions, 3));

        // 平滑后重新着色，避免颜色与高度不一致
        const recolored = [];
        for (let i = 0; i < smoothedPositions.length; i += 3) {
            const x = smoothedPositions[i];
            const y = smoothedPositions[i + 1];
            const z = smoothedPositions[i + 2];
            const distanceFromCenter = Math.sqrt(x * x + z * z);
            this.addVertexColorWithBeach(recolored, y, distanceFromCenter, x, z);
        }
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(recolored, 3));

        // 重新计算法线
        geometry.computeVertexNormals();

        console.log('🔧 已应用几何体平滑处理');
    }

    /**
     * 拉普拉斯平滑算法（基于拓扑邻接，适用于不规则网格）
     */
    laplacianSmooth(positions, geometry) {
        const indexAttr = geometry.getIndex();
        const posCount = positions.length / 3;
        const newPositions = new Float32Array(positions.length);

        // 初始化输出为原始位置
        for (let i = 0; i < positions.length; i++) newPositions[i] = positions[i];

        if (!indexAttr) {
            // 无索引网格：无法构建拓扑邻接，直接返回
            return;
        }

        // 构建邻接列表
        const neighbors = new Array(posCount);
        for (let i = 0; i < posCount; i++) neighbors[i] = new Set();

        const indices = indexAttr.array;
        for (let i = 0; i < indices.length; i += 3) {
            const a = indices[i];
            const b = indices[i + 1];
            const c = indices[i + 2];

            neighbors[a].add(b); neighbors[a].add(c);
            neighbors[b].add(a); neighbors[b].add(c);
            neighbors[c].add(a); neighbors[c].add(b);
        }

        const smoothFactor = 0.7; // 更强平滑强度（向邻接平均靠拢）

        // 对每个顶点应用平滑（只调整 Y，高度）
        for (let v = 0; v < posCount; v++) {
            const i3 = v * 3;
            const curY = positions[i3 + 1];

            const nbrs = neighbors[v];
            if (nbrs.size === 0) continue;

            let avgY = 0;
            let cnt = 0;
            for (const n of nbrs) {
                avgY += positions[n * 3 + 1];
                cnt++;
            }
            avgY /= cnt;

            // 高处更强平滑，针对山体进一步圆润
            const heightNorm = Math.max(0, Math.min(1, curY / (this.heightScale || 1)));
            const factor = Math.min(0.95, smoothFactor * (0.8 + 0.8 * heightNorm));
            const newY = curY * (1 - factor) + avgY * factor;
            newPositions[i3 + 1] = Math.max(0, newY); // 不低于海平面
        }

        // 写回
        for (let i = 0; i < positions.length; i++) positions[i] = newPositions[i];
    }
}
